import { useHealth, useShutdown } from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { AlertCircle, ArrowLeft, Calendar, Clock, Power, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui';

/**
 * Server Info page - displays health and uptime
 */
export function ServerInfoPage() {
  const { data: health, isLoading, isError, refetch } = useHealth();
  const { mutate: shutdown, isPending: isShuttingDown } = useShutdown();
  const [showConfirm, setShowConfirm] = useState(false);

  const canShutdown = health?.capabilities?.remoteShutdown === true;

  const handleShutdown = () => {
    shutdown(undefined, {
      onSuccess: () => {
        setShowConfirm(false);
      },
    });
  };

  const formatUptime = (uptime: string | undefined): string => {
    if (!uptime) return 'Unknown';

    // Parse Go duration format like "2h30m15s" or "72h5m10.123456s"
    const hours = uptime.match(/(\d+)h/)?.[1];
    const minutes = uptime.match(/(\d+)m/)?.[1];
    const seconds = uptime.match(/(\d+(?:\.\d+)?)s/)?.[1];

    const parts: string[] = [];
    if (hours) {
      const h = parseInt(hours, 10);
      if (h >= 24) {
        const days = Math.floor(h / 24);
        const remainingHours = h % 24;
        parts.push(`${days}d`);
        if (remainingHours > 0) parts.push(`${remainingHours}h`);
      } else {
        parts.push(`${h}h`);
      }
    }
    if (minutes) parts.push(`${minutes}m`);
    if (seconds && !hours) parts.push(`${Math.floor(parseFloat(seconds))}s`);

    return parts.join(' ') || uptime;
  };

  const formatStartTime = (startTime: string | undefined): string => {
    if (!startTime) return 'Unknown';
    try {
      const date = new Date(startTime);
      return date.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return startTime;
    }
  };

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Server Info
        </h1>
        <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
          System health and status information
        </p>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch server status</h3>
              <p className="mt-1 text-sm text-red-400/70">
                The server may be unavailable or there was a connection error.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Health data */}
      {!isLoading && !isError && health && (
        <>
          {/* Status overview */}
          <div>
            <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Status
            </h2>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <div
                    className={`h-3 w-3 rounded-full ${
                      health.status === 'ok'
                        ? 'bg-emerald-400 shadow-lg shadow-emerald-400/30'
                        : 'bg-amber-400 shadow-lg shadow-amber-400/30'
                    }`}
                  />
                  <div>
                    <div className="text-[15px] font-medium text-gray-900 dark:text-white">
                      Server Status
                    </div>
                    <div className="mt-0.5 text-sm capitalize text-gray-500 dark:text-zinc-500">
                      {health.status}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
                <div className="flex items-center gap-4">
                  <Clock className="h-5 w-5 text-gray-500 dark:text-zinc-500" />
                  <div>
                    <div className="text-[15px] font-medium text-gray-900 dark:text-white">
                      Uptime
                    </div>
                    <div className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                      {formatUptime(health.uptime)}
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
                <div className="flex items-center gap-4">
                  <Calendar className="h-5 w-5 text-gray-500 dark:text-zinc-500" />
                  <div>
                    <div className="text-[15px] font-medium text-gray-900 dark:text-white">
                      Started
                    </div>
                    <div className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                      {formatStartTime(health.startTime)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Server Actions */}
          <div>
            <h2 className="mb-4 text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Actions
            </h2>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <Power className="h-5 w-5 text-gray-500 dark:text-zinc-500" />
                  <div>
                    <div className="text-[15px] font-medium text-gray-900 dark:text-white">
                      Restart Server
                    </div>
                    <div className="mt-0.5 text-sm text-gray-500 dark:text-zinc-500">
                      {canShutdown
                        ? 'Gracefully restart the server'
                        : 'Remote shutdown is not enabled in server config'}
                    </div>
                  </div>
                </div>
                <div className="relative">
                  {showConfirm ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-zinc-400">
                        Are you sure?
                      </span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleShutdown}
                        loading={isShuttingDown}
                      >
                        Confirm
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setShowConfirm(false)}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setShowConfirm(true)}
                      disabled={!canShutdown}
                      leftIcon={<Power className="h-4 w-4" />}
                    >
                      Restart
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Refresh button */}
          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={() => refetch()}
              leftIcon={<RefreshCw className="h-4 w-4" />}
            >
              Refresh
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
